/**
 * <caos-hero-sequence>: the home hero, driven by GSAP + ScrollTrigger. No text: only the snake.
 *
 * 1. After the load event every frame is fetched and decoded once into an ImageBitmap (they never
 *    compete with the LCP image). Until all of them are ready the still image stays on screen.
 * 2. Intro: once the frames are ready, a timeline plays the first ones by itself in 2s, then stops.
 *    If the reader has already scrolled, or scrolls meanwhile, the scroll takes over.
 * 3. Scroll: the section is pinned; a ScrollTrigger with a soft scrub drives the remaining frames.
 *    The position is fractional: between two frames the next one is blended on top of the previous
 *    one, so 60 frames read as a continuous movement instead of 60 steps.
 * 4. Drawing happens at most once per animation frame (requestAnimationFrame), never inside the
 *    scroll handler, and only when the position really changed.
 * 5. While the hero is on screen the header keeps no background (class caos-hero-on on <html>).
 * 6. Phones (under 768px) get the vertical frames too, drawn without the blend and without
 *    ImageBitmaps to keep memory low.
 *
 * The pin runs in "transform" mode: with .page-wrapper as scroller (Horizon, from 990px up) a fixed
 * pin sends the wheel to the document, which has overflow hidden.
 *
 * Fallbacks: without JavaScript or with prefers-reduced-motion the still image is shown and nothing
 * moves. In the theme editor nothing moves either. Frame URLs come from the JSON script inside the element.
 */
class CaosHeroSequence extends HTMLElement {
  /** @type {any} */
  #mm;

  connectedCallback() {
    if (this.#mm) return;
    const { gsap, ScrollTrigger } = /** @type {any} */ (window);
    const design = /** @type {any} */ (window).Shopify?.designMode;
    const root = document.documentElement;

    const hideHint = () => root.classList.add('caos-hero-scrolled');
    const pageScroller = document.querySelector('.page-wrapper');
    window.addEventListener('scroll', hideHint, { once: true, passive: true });
    window.addEventListener('wheel', hideHint, { once: true, passive: true });
    window.addEventListener('touchmove', hideHint, { once: true, passive: true });
    pageScroller?.addEventListener('scroll', hideHint, { once: true, passive: true });

    if (!gsap || !ScrollTrigger || design) return;

    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    const frameData = JSON.parse(this.querySelector('[data-frames]')?.textContent || '{}');
    const stage = /** @type {HTMLElement} */ (this.querySelector('.caos-hero__stage'));
    const canvas = /** @type {HTMLCanvasElement} */ (this.querySelector('.caos-hero__canvas'));
    const ctx = canvas?.getContext('2d', { alpha: false });
    if (!stage || !canvas || !ctx) return;

    const introShare = Number(this.dataset.introShare || 0.2);
    const scrollDesktop = Number(this.dataset.scrollDesktop || 150);
    const scrollMobile = Number(this.dataset.scrollMobile || 120);

    this.#mm = gsap.matchMedia();
    this.#mm.add(
      {
        isSmall: '(max-width: 767px)',
        isLarge: '(min-width: 768px)',
        isWide: '(min-width: 990px)',
        isPortrait: '(orientation: portrait)',
        reduce: '(prefers-reduced-motion: reduce)',
      },
      (/** @type {any} */ context) => {
        const { isSmall, isWide, isPortrait, reduce } = context.conditions;
        if (reduce) return;

        const scroller = isWide ? document.querySelector('.page-wrapper') || window : window;

        /** @type {string[]} */
        // Phones and tablets held upright get the vertical series (604x1076), everything else the 16:9 one
        const urls = (isSmall || isPortrait ? frameData.mobile : frameData.desktop) || [];
        const count = urls.length;
        if (!count) return;

        const introEnd = (count - 1) * introShare;
        /** @type {(ImageBitmap|HTMLImageElement)[]} */
        let frames = [];
        const state = { intro: 0, scroll: introEnd };
        let drawn = -1;
        let ready = false;
        let scrolled = false;
        let reverted = false;
        let rafId = 0;
        /** @type {any} */
        let introTimeline;
        // Cover geometry, computed once per canvas size (all frames share the same size)
        let geo = { x: 0, y: 0, w: 0, h: 0 };
        // Blending two frames doubles the drawing work: if this machine starts dropping frames while
        // the sequence moves, the blend is switched off and it goes back to whole frames.
        // Phones: no blend and plain decoded images instead of ImageBitmaps, to keep memory low.
        let blend = !isSmall;
        let slowPaints = 0;
        let lastPaint = 0;

        const position = () =>
          Math.min(count - 1, Math.max(0, state.intro + state.scroll - introEnd));

        const paint = (/** @type {number} */ now) => {
          rafId = 0;
          if (blend && lastPaint && now - lastPaint > 28 && now - lastPaint < 200 && ++slowPaints > 6) blend = false;
          lastPaint = now;
          if (!ready) return;
          // Quantised to 1/32 of a frame: below that the eye sees no difference
          const pos = Math.round(position() * 32) / 32;
          if (pos === drawn) return;
          const target = blend ? pos : Math.round(pos);
          if (target === drawn) return;
          drawn = target;
          const base = Math.floor(target);
          const mix = target - base;
          ctx.globalAlpha = 1;
          ctx.drawImage(frames[base], geo.x, geo.y, geo.w, geo.h);
          if (mix > 0 && base + 1 < count) {
            ctx.globalAlpha = mix;
            ctx.drawImage(frames[base + 1], geo.x, geo.y, geo.w, geo.h);
            ctx.globalAlpha = 1;
          }
          this.dataset.frame = String(Math.round(target) + 1);
        };

        const draw = () => {
          if (!rafId) rafId = requestAnimationFrame(paint);
        };

        // The canvas never gets more pixels than the frames themselves (1600 wide): CSS scales it up on
        // the GPU, which costs nothing, while every pixel drawn by hand costs main-thread time.
        const sizeCanvas = () => {
          const first = frames[0];
          const frameW = first ? ('naturalWidth' in first ? first.naturalWidth : first.width) : 1600;
          const dpr = Math.min(window.devicePixelRatio || 1, 1.5, frameW / Math.max(1, canvas.clientWidth));
          const width = Math.round(canvas.clientWidth * dpr);
          const height = Math.round(canvas.clientHeight * dpr);
          if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
          }
          if (first) {
            const fw = 'naturalWidth' in first ? first.naturalWidth : first.width;
            const fh = 'naturalHeight' in first ? first.naturalHeight : first.height;
            const scale = Math.max(width / fw, height / fh);
            geo = { w: fw * scale, h: fh * scale, x: (width - fw * scale) / 2, y: (height - fh * scale) / 2 };
          }
          ctx.imageSmoothingQuality = 'medium';
          drawn = -1;
          draw();
        };

        const endIntro = () => {
          if (state.intro >= introEnd) return;
          introTimeline?.kill();
          gsap.to(state, { intro: introEnd, duration: 0.35, ease: 'power2.out', onUpdate: draw });
        };

        const onFramesReady = () => {
          if (reverted) return;
          ready = true;
          sizeCanvas();
          gsap.set(canvas, { autoAlpha: 1 });
          ScrollTrigger.refresh();
          if (scrolled) {
            state.intro = introEnd;
            draw();
            return;
          }
          introTimeline = gsap.timeline();
          introTimeline.to(state, { intro: introEnd, duration: 2, ease: 'power2.inOut', onUpdate: draw });
        };

        // Fetch every frame and decode it once, off the main thread where the browser allows it.
        // ImageBitmaps are drawn without any further decoding work during the scroll.
        const toBitmap = async (/** @type {string} */ url) => {
          const img = new Image();
          img.decoding = 'async';
          img.src = url;
          await img.decode();
          if ('createImageBitmap' in window) {
            try {
              return await createImageBitmap(img);
            } catch (e) {
              return img;
            }
          }
          return img;
        };

        const toImage = async (/** @type {string} */ url) => {
          const img = new Image();
          img.decoding = 'async';
          img.src = url;
          await img.decode();
          return img;
        };

        // A frame that fails to load is replaced by the nearest one that loaded: the sequence still
        // starts, with a repeated frame instead of a hole. Only the first frame is required.
        const loadFrames = () => {
          Promise.allSettled(urls.map(isSmall ? toImage : toBitmap)).then((results) => {
            if (reverted) return;
            const list = results.map((r) => (r.status === 'fulfilled' ? r.value : null));
            if (!list[0]) return;
            for (let i = 1; i < list.length; i++) if (!list[i]) list[i] = list[i - 1];
            frames = /** @type {any} */ (list);
            onFramesReady();
          });
        };

        ScrollTrigger.create({
          trigger: stage,
          scroller,
          pin: true,
          pinType: isWide ? 'transform' : 'fixed',
          anticipatePin: 1,
          start: 'top top',
          end: `+=${isPortrait ? scrollMobile : scrollDesktop}%`,
          scrub: 0.9,
          snap: false,
          animation: gsap.to(state, { scroll: count - 1, ease: 'none', onUpdate: draw }),
          onRefresh: sizeCanvas,
          onUpdate: (/** @type {any} */ self) => {
            if (!scrolled && self.scroll() - self.start > 4) {
              scrolled = true;
              root.classList.add('caos-hero-scrolled');
              endIntro();
            }
            root.classList.toggle('caos-hero-on', self.progress < 0.999);
          },
          onLeave: () => root.classList.remove('caos-hero-on'),
          onEnterBack: () => root.classList.add('caos-hero-on'),
        });

        root.classList.add('caos-hero-on');

        if (document.readyState === 'complete') loadFrames();
        else window.addEventListener('load', loadFrames, { once: true });

        return () => {
          reverted = true;
          if (rafId) cancelAnimationFrame(rafId);
          window.removeEventListener('load', loadFrames);
          root.classList.remove('caos-hero-on');
          frames.forEach((f) => f && 'close' in f && f.close());
        };
      }
    );

    document.fonts.ready.then(() => ScrollTrigger.refresh());
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
    }
  }

  disconnectedCallback() {
    queueMicrotask(() => {
      if (this.isConnected) return;
      this.#mm?.revert();
      this.#mm = undefined;
    });
  }
}

if (!customElements.get('caos-hero-sequence')) {
  customElements.define('caos-hero-sequence', CaosHeroSequence);
}
